import type { ViewerShell } from '../../shell';
import { RisError } from '../client';
import { AI_LIMIT_TEXT, aiLimitText, streamErrorText } from './errors';
import { type AssistantApi, type AssistantFinding, type AssistantMessage, type AskBody, type AssistantMode, StreamFailed, StreamInterrupted } from './api';
import { conversationStudy, measurementContext, priorStudies, viewportContext } from './context';
import { setFindings } from './findings-overlay';
import { activeHeaderAnswer } from './header-answers';
import { LiveAnswer } from './live-answer';
import { type MessageHandlers, renderMessage } from './messages';
import { VoiceButton } from './voice';

/**
 * The Asistente tab: banner, conversation, quick chips, input with dictation. Each question carries
 * the image on the active viewport (series, instance, frame, position) and its window; header
 * questions are answered locally when the header has the value. Reviews and actions are handled by
 * the `MessageHandlers` the module passes in.
 */

export const PANEL_TEXT = {
  failed: 'No fue posible obtener respuesta del Asistente Guru.',
  banner: 'Asistente Guru · no firma · no decide',
  testProvider: 'Proveedor de prueba, resultado no clínico',
  empty: 'Pregunte sobre la imagen actual o use una sugerencia. Las respuestas son hallazgos sugeridos para su revisión.',
  placeholder: 'Escriba una pregunta…',
  inputLabel: 'Pregunta para el Asistente Guru',
  send: 'Enviar',
  thinking: 'pensando…',
  historyError: 'No fue posible cargar la conversación con el Asistente Guru.',
  noPrior: 'No hay un estudio previo cargado en el visor.',
  tools: 'Herramientas del Asistente Guru',
  interrupted: 'La respuesta del Asistente Guru se interrumpió; intenta de nuevo.',
} as const;

/** ris without `/assistant/stream` (older deploy): ask on the plain route from then on. */
const NO_STREAM_STATUSES = new Set([404, 405]);

/**
 * The hook other assistant modules (series analysis, report, similar cases, VIEW-B's tools) use:
 * a button in the tools row and a card in the conversation.
 */
export interface AssistantPanelHook {
  addToolButton(label: string, onClick: () => void): HTMLButtonElement;
  /** `study`: the conversation the card belongs to (default: the current one), for a result that arrives late. */
  appendResult(node: HTMLElement, study?: string): HTMLElement;
  /** Takes a card out for good: a history reload never brings it back. */
  removeResult(node: HTMLElement): void;
}

/** Answers true when it took the "Revisar serie" chip over (else the chip asks the plain sweep). */
export type SweepHandler = () => Promise<boolean>;

const MAX_MESSAGE = 1000;

interface Chip {
  label: string;
  mode: AssistantMode;
  message: string;
  needsPrior?: boolean;
}

export const CHIPS: readonly Chip[] = [
  { label: '¿Qué ves aquí?', mode: 'describe', message: '¿Qué ves en esta imagen?' },
  { label: 'Revisar serie', mode: 'sweep', message: 'Revisa la serie completa.' },
  { label: 'Comparar con previo', mode: 'compare', message: 'Compara con el estudio previo.', needsPrior: true },
  { label: 'Resumir previos', mode: 'prior_summary', message: 'Resume los estudios previos.' },
  { label: '¿Qué más mirar?', mode: 'checklist', message: '¿Qué más debería revisar en este estudio?' },
  { label: 'Calidad', mode: 'quality', message: 'Evalúa la calidad técnica de la adquisición.' },
  { label: 'Diferenciales', mode: 'differentials', message: '¿Qué diferenciales considerar?' },
  { label: 'Explicar', mode: 'chat', message: 'Explica con más detalle tu respuesta anterior.' },
];

/** What a failed question tells the user, by ris status. */
export function askErrorText(error: unknown): string {
  if (error instanceof StreamFailed) return streamErrorText(error.status, error.detail) ?? PANEL_TEXT.failed;
  const limit = aiLimitText(error);
  if (limit) return limit;
  if (error instanceof StreamInterrupted) return PANEL_TEXT.interrupted;
  if (error instanceof RisError) {
    if (error.status === 401 || error.status === 403) return 'No dispone de permisos para usar el Asistente Guru.';
    if (error.status === 402) return AI_LIMIT_TEXT.noBalance;
    if (error.status === 409) return 'El Asistente Guru no está habilitado para esta organización.';
    if (error.status === 422) return 'La pregunta no es válida.';
    if (error.status === 503) return AI_LIMIT_TEXT.unavailable;
  }
  return PANEL_TEXT.failed;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export class AssistantPanel implements AssistantPanelHook {
  readonly root = el('section', 'assistant-panel');
  private readonly testLabel = el('div', 'assistant-test-banner', PANEL_TEXT.testProvider);
  private readonly log = el('div', 'assistant-log');
  private readonly chipBar = el('div', 'assistant-chips');
  private readonly toolBar = el('div', 'assistant-tools');
  private readonly input = el('textarea', 'assistant-input');
  private readonly sendButton = el('button', 'assistant-btn assistant-send', PANEL_TEXT.send);
  private readonly voice: VoiceButton;
  private loadedStudy: string | null = null;
  private visible = false;
  private busy = false;
  private hasTest = false;
  /** The history request in flight (or done): a question waits for it, so the history cannot wipe its answer. */
  private historyLoad: Promise<void> = Promise.resolve();
  /** False once ris answered that it has no streaming route. */
  private streamable = true;
  private sweepHandler: SweepHandler | null = null;
  /** Tool cards per conversation study: a history reload (another study, then back) shows them again. */
  private readonly results = new Map<string, HTMLElement[]>();
  /** Told each time the log loads a study's conversation (radiologist tools: patient context). */
  private readonly conversationListeners: Array<(study: string) => void> = [];

  constructor(
    private readonly shell: ViewerShell,
    private readonly api: AssistantApi,
    private readonly handlers: MessageHandlers,
  ) {
    this.voice = new VoiceButton(shell, api, (text) => this.insertDictation(text), () => conversationStudy(shell));
    this.build();
    shell.on('active-changed', () => this.studyMaybeChanged());
    shell.on('series-shown', () => this.studyMaybeChanged());
    shell.on('studies-changed', () => this.updateChips());
  }

  private build(): void {
    const banner = el('div', 'assistant-banner');
    banner.setAttribute('role', 'note');
    banner.appendChild(el('strong', 'assistant-banner-title', PANEL_TEXT.banner));
    this.testLabel.hidden = true;
    banner.appendChild(this.testLabel);
    this.log.setAttribute('role', 'log');
    this.log.setAttribute('aria-live', 'polite');
    this.log.setAttribute('aria-label', 'Conversación con el Asistente Guru');
    this.log.tabIndex = 0;
    this.chipBar.setAttribute('role', 'group');
    this.chipBar.setAttribute('aria-label', 'Sugerencias');
    for (const chip of CHIPS) {
      const b = el('button', 'assistant-chip', chip.label);
      b.type = 'button';
      b.dataset.mode = chip.mode;
      b.addEventListener('click', () => void this.chip(chip));
      this.chipBar.appendChild(b);
    }
    this.toolBar.setAttribute('role', 'group');
    this.toolBar.setAttribute('aria-label', PANEL_TEXT.tools);
    this.toolBar.hidden = true;
    this.root.append(banner, this.log, this.chipBar, this.toolBar, this.buildForm());
    this.updateChips();
  }

  private buildForm(): HTMLFormElement {
    const form = el('form', 'assistant-form');
    this.input.rows = 2;
    this.input.maxLength = MAX_MESSAGE;
    this.input.placeholder = PANEL_TEXT.placeholder;
    this.input.setAttribute('aria-label', PANEL_TEXT.inputLabel);
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
        event.preventDefault();
        form.requestSubmit();
      }
    });
    this.sendButton.type = 'submit';
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.ask(this.input.value);
    });
    const row = el('div', 'assistant-form-row');
    if (this.voice.element) row.appendChild(this.voice.element);
    row.appendChild(this.sendButton);
    form.append(this.input, row);
    if (this.voice.keptElement) form.appendChild(this.voice.keptElement);
    return form;
  }

  /** A button in the tools row (under the chips): series analysis, report, similar cases, VIEW-B's tools. */
  addToolButton(label: string, onClick: () => void): HTMLButtonElement {
    const b = el('button', 'assistant-chip assistant-tool', label);
    b.type = 'button';
    b.addEventListener('click', onClick);
    this.toolBar.appendChild(b);
    this.toolBar.hidden = false;
    return b;
  }

  /**
   * A card in the conversation (a tool's form or result) of the current study. It outlives history
   * reloads: switching to another study's conversation and back shows it again, still live.
   */
  appendResult(node: HTMLElement, study: string = conversationStudy(this.shell)): HTMLElement {
    node.classList.add('assistant-result');
    this.forgetResult(node);
    this.results.set(study, [...(this.results.get(study) ?? []), node]);
    // Another study's conversation is on screen: the card shows when that study's comes back.
    if (study !== this.shownStudy()) return node;
    this.log.querySelector('.assistant-empty')?.remove();
    this.log.appendChild(node);
    node.scrollIntoView?.({ block: 'nearest' });
    return node;
  }

  removeResult(node: HTMLElement): void {
    this.forgetResult(node);
    node.remove();
  }

  private forgetResult(node: HTMLElement): void {
    for (const [study, cards] of this.results) {
      if (cards.includes(node)) this.results.set(study, cards.filter((n) => n !== node));
    }
  }

  /** The study whose conversation the log holds (or is loading). */
  private shownStudy(): string {
    return this.loadedStudy ?? conversationStudy(this.shell);
  }

  /**
   * Stored messages a tool got from ris (a region question): shown as conversation messages, so the
   * history reload, which brings the stored copies, replaces them instead of doubling them.
   */
  appendMessages(messages: AssistantMessage[], study: string): void {
    if (study === this.shownStudy()) messages.forEach((m) => this.append(m));
  }

  /** "Revisar serie" runs a large-study analysis when ris offers one (index.ts sets it). */
  setSweepHandler(handler: SweepHandler | null): void {
    this.sweepHandler = handler;
  }

  private async chip(chip: Chip): Promise<void> {
    if (chip.mode === 'sweep' && this.sweepHandler && !this.busy && await this.sweepHandler()) return;
    await this.ask(chip.message, chip.mode);
  }

  /** The tab was shown or hidden. */
  setVisible(visible: boolean): void {
    this.visible = visible;
    if (!visible) {
      // A dictation in progress is finalized (transcribed, or kept on failure), never discarded.
      void this.voice.finalize();
      return;
    }
    this.updateChips();
    if (this.loadedStudy !== conversationStudy(this.shell)) void this.loadHistory();
  }

  private studyMaybeChanged(): void {
    if (this.visible && this.loadedStudy !== null && this.loadedStudy !== conversationStudy(this.shell)) void this.loadHistory();
  }

  private updateChips(): void {
    const hasPrior = priorStudies(this.shell, conversationStudy(this.shell)).length > 0;
    this.chipBar.querySelectorAll<HTMLButtonElement>('.assistant-chip').forEach((b) => {
      const needsPrior = CHIPS.find((c) => c.mode === b.dataset.mode)?.needsPrior === true;
      b.disabled = this.busy || (needsPrior && !hasPrior);
      b.title = needsPrior && !hasPrior ? PANEL_TEXT.noPrior : '';
    });
  }

  private setBusy(busy: boolean): void {
    this.busy = busy;
    this.sendButton.disabled = busy;
    this.root.setAttribute('aria-busy', String(busy));
    this.updateChips();
  }

  loadHistory(): Promise<void> {
    this.historyLoad = this.fetchHistory();
    // fetchHistory set loadedStudy before its first await: that is the study now on screen.
    const study = this.loadedStudy;
    if (study !== null) this.conversationListeners.forEach((listen) => listen(study));
    return this.historyLoad;
  }

  /**
   * `listener` runs each time the Asistente opens a study's conversation (the tab shown on it, or
   * the reader moved to another study), and at once for the conversation already on screen.
   */
  onConversation(listener: (study: string) => void): void {
    this.conversationListeners.push(listener);
    if (this.loadedStudy !== null) listener(this.loadedStudy);
  }

  private async fetchHistory(): Promise<void> {
    const study = conversationStudy(this.shell);
    this.loadedStudy = study;
    let history: AssistantMessage[];
    try {
      history = await this.api.history(study);
    } catch (error) {
      console.warn('[viewer] assistant history failed', error);
      this.shell.toast(PANEL_TEXT.historyError, 'error');
      history = [];
    }
    if (this.loadedStudy !== study) return;
    this.log.replaceChildren();
    this.hasTest = false;
    const cards = this.results.get(study) ?? [];
    if (history.length === 0 && cards.length === 0) this.log.appendChild(el('p', 'assistant-empty', PANEL_TEXT.empty));
    history.forEach((m) => this.append(m));
    this.log.append(...cards);
  }

  private append(message: AssistantMessage, local = false): HTMLElement {
    this.log.querySelector('.assistant-empty')?.remove();
    const node = renderMessage(message, this.handlers, local);
    this.log.appendChild(node);
    if (message.findings.length > 0) setFindings(message.findings);
    if (message.isTest && !this.hasTest) this.hasTest = true;
    this.testLabel.hidden = !this.hasTest;
    node.scrollIntoView?.({ block: 'nearest' });
    return node;
  }

  /** Replaces every card of `finding` (after a decision / undo). */
  replaceFinding(finding: AssistantFinding, render: (f: AssistantFinding) => HTMLElement): void {
    this.log.querySelectorAll<HTMLElement>(`.assistant-finding[data-finding-id="${CSS.escape(finding.id)}"]`)
      .forEach((card) => card.replaceWith(render(finding)));
  }

  private insertDictation(text: string): void {
    this.input.value = [this.input.value.trim(), text.trim()].filter(Boolean).join(' ').slice(0, MAX_MESSAGE);
    this.input.focus();
  }

  private localMessage(role: 'user' | 'assistant', content: string): AssistantMessage {
    return { id: '', role, content, createdAt: null, mode: 'header', isTest: false, model: null, modelVersion: null, findings: [], actions: [], analysed: null };
  }

  private body(message: string, mode: AssistantMode | undefined, study: string): AskBody {
    const body: AskBody = { message };
    if (mode) body.mode = mode;
    const viewport = viewportContext(this.shell, study);
    if (viewport) body.viewport = viewport;
    const prior = priorStudies(this.shell, study)[0];
    if (prior && (mode === 'compare' || mode === 'prior_summary')) body.prior_study_uid = prior.studyInstanceUID;
    const measurements = measurementContext(study);
    if (measurements.length > 0) body.measurements = measurements;
    return body;
  }

  /** Sends a question (typed or a chip). Typed header questions may be answered here instead. */
  async ask(text: string, chipMode?: AssistantMode): Promise<void> {
    const message = text.trim().slice(0, MAX_MESSAGE);
    if (!message || this.busy) return;
    // Busy from here: the header lookup and the history may await, and a second Enter must not ask twice.
    this.setBusy(true);
    try {
      let mode = chipMode;
      if (!mode) {
        const header = await activeHeaderAnswer(this.shell, message);
        if (header.answer) {
          this.input.value = '';
          this.append(this.localMessage('user', message));
          this.append(this.localMessage('assistant', header.answer), true);
          return;
        }
        if (header.matched) mode = 'header';
      }
      await this.send(message, mode, chipMode === undefined);
    } finally {
      this.setBusy(false);
    }
  }

  /** `typed`: the question came from the input (cleared now, given back if the question fails). */
  private async send(message: string, mode: AssistantMode | undefined, typed: boolean): Promise<void> {
    const study = conversationStudy(this.shell);
    await (this.loadedStudy !== study ? this.loadHistory() : this.historyLoad);
    if (typed) this.input.value = '';
    const pending = this.append(this.localMessage('user', message));
    pending.classList.add('pending');
    const thinking = el('div', 'assistant-thinking', PANEL_TEXT.thinking);
    thinking.setAttribute('role', 'status');
    this.log.appendChild(thinking);
    const live = new LiveAnswer(this.log, thinking);
    try {
      const out = await this.answer(study, this.body(message, mode, study), live);
      live.remove();
      // The stream's final may carry the answer alone: the question then stays as it was shown.
      if (out.some((m) => m.role === 'user')) pending.remove();
      else pending.classList.remove('pending');
      if (this.loadedStudy === study) out.forEach((m) => this.append(m));
    } catch (error) {
      console.warn('[viewer] assistant question failed', error);
      live.remove();
      pending.remove();
      if (typed && !this.input.value) this.input.value = message;
      this.shell.toast(askErrorText(error), 'error');
    } finally {
      thinking.remove();
    }
  }

  /** Streams the answer when ris can; a ris without the stream route gets the plain POST (once known, always). */
  private async answer(study: string, body: AskBody, live: LiveAnswer): Promise<AssistantMessage[]> {
    if (this.streamable) {
      try {
        return await this.api.askStream(study, body, (text) => live.add(text));
      } catch (error) {
        if (!(error instanceof RisError && NO_STREAM_STATUSES.has(error.status) && live.empty)) throw error;
        this.streamable = false;
      }
    }
    return this.api.ask(study, body);
  }
}
